<template>
    <div class="profile-experience-item" :class="`profile-experience-item--${size}`">
        <MaltImg
            v-if="experience.logoSrc"
            class="profile-experience-item__icon"
            :class="`profile-experience-item__icon--${size}`"
            fit="contain"
            width="46"
            height="46"
            :src="experience.logoSrc"
            alt=""
        />
        <VJoyIcon
            v-else
            :size="size"
            name="company-placeholder"
            class="profile-experience-item__icon"
            :class="`profile-experience-item__icon--${size}`"
        />
        <div class="profile-experience-item__content">
            <div class="profile-experience-item__content__title" :class="`profile-experience-item__content__title--${size}`">
                <div
                    class="profile-experience-item__content__title__company"
                    data-testid="profile-experience-item-company"
                    :title="experience.company"
                >
                    {{ experience.company }}
                </div>
                <div
                    v-if="experience.missionId"
                    class="profile-experience-item__content__on-malt-badge"
                    :class="`profile-experience-item__content__on-malt-badge--${size}`"
                >
                    <MaltImg
                        src="/brand/malt_logo_white-resize"
                        :alt="t('profile.section.experiences.malt.logo')"
                        :width="size === ExperienceSize.LARGE ? 20 : 16"
                        :height="size === ExperienceSize.LARGE ? 20 : 16"
                        fit="cover"
                    />
                    <span class="profile-experience-item__content__on-malt-text" :class="`profile-experience-item__content__on-malt-text--${size}`">
                        {{ t('profile.section.experiences.on.malt') }}
                    </span>
                </div>
            </div>
            <div class="profile-experience-item__content__subtitle" data-testid="profile-experience-item-job">
                {{ experience.title }}
            </div>
            <div
                v-if="showActivityArea"
                class="profile-experience-item__content__activity-area joy-mb-4"
                data-testid="profile-experience-item-activity-area"
            >
                {{ t(`company.activity.area.${experience.activityArea}`).toUpperCase() }}
            </div>
            <div class="profile-experience-item__content__others">
                <ExperienceItemDateRange
                    v-if="showDateRange"
                    :start-date="experience.startDate!"
                    :end-date="experience.endDate"
                    :current="experience.currentJob"
                />
                <template v-if="showLocation">
                    <div data-testid="profile-experience-item-location">
                        {{ experience.location }}
                    </div>
                </template>
            </div>
            <div class="profile-experience-item__content__description">
                <SeeMoreOrRedirect
                    v-if="showSeeMoreOnDescription && showDescription"
                    :max-lines="4"
                    :content="sanitizedDescription"
                    :default-state="isAdmin ? 'expanded' : 'collapsed'"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <div v-if="showDescription" data-testid="profile-experience-item-description" v-html="sanitizedDescription" />
                </SeeMoreOrRedirect>
                <!-- eslint-disable vue/no-v-html -->
                <div v-else-if="showDescription" data-testid="profile-experience-item-description" v-html="sanitizedDescription" />
            </div>

            <VJoyTagsList v-if="showSkills" class="joy-mt-4" variant="secondary" :tags="formatSkills(experience.skills)" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {MaltImg} from '#components';
    import {useHasEnoughPriviledge, useMarkdownToHtml, useTranslation} from '#imports';
    import {VJoyIcon, VJoyTagsList} from '@maltjoy/core-vue';
    import type {ExperienceItem} from '~/components/_common/experience/ExperienceItem';
    import {ExperienceSize} from '~/components/_common/experience/ExperienceItem';
    import ExperienceItemDateRange from '~/components/_common/experience/ExperienceItemDateRange.vue';
    import SeeMoreOrRedirect from '~/components/_common/see-more/SeeMoreOrRedirect.vue';
    import {overrideH1ToH4} from '~/config/mdToHtmlOverrides';
    import {useFeatureContextStore} from '~/store/featureStore';

    const {t} = useTranslation();
    const {markdownToHtml} = useMarkdownToHtml();
    const isAdmin = await useHasEnoughPriviledge(['ROLE_ADMIN']);

    export interface Props {
        size?: ExperienceSize;
        experience: ExperienceItem;
        titleMarginRight?: number;
        mf?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        size: ExperienceSize.LARGE,
        titleMarginRight: 0,
        mf: false,
    });

    const {features} = storeToRefs(useFeatureContextStore());

    const showActivityArea = computed(() => !!props.experience.activityArea);
    const showLocation = computed(() => !!props.experience.location);
    const showDateRange = computed(() => !!props.experience.startDate);
    const showDescription = computed(() => !!props.experience.description);
    const showSkills = computed(() => props.experience.skills.length > 0);
    const showSeeMoreOnDescription = computed(() =>
        props.mf ? features.value['profile-revamp-drawer'] && features.value['profile-revamp-drawer-see-more'] : features.value['profile-revamp'],
    );
    const sanitizedDescription = computed(() => markdownToHtml(props.experience.description, overrideH1ToH4));

    function formatSkills(skills?: string[]) {
        return (
            skills?.map((skill) => {
                return {
                    value: skill.toLowerCase(),
                    label: skill,
                };
            }) || []
        );
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint' as joy;

    .profile-experience-item {
        display: flex;
        flex: 1 1 auto;

        &--small {
            @include mq.screen_xs() {
                flex-direction: row;
            }

            @include mq.screen_xxs() {
                flex-direction: column-reverse;
            }
        }

        &__icon {
            &--large {
                @include mq.screen_tiny() {
                    display: none;
                }
            }

            &--small {
                @include mq.screen_xs() {
                    display: block;
                }

                @include mq.screen_xxs() {
                    display: none;
                }
            }
        }

        &__content {
            display: flex;
            flex: 1;
            flex-direction: column;
            justify-content: space-between;
            margin-left: var(--joy-core-spacing-4);
            min-width: 0; // needed to make the text ellipsis work

            &__title {
                display: flex;
                margin-bottom: var(--joy-core-spacing-1);
                max-width: 100%;
                gap: var(--joy-core-spacing-2);
                font-family: var(--joy-font-family-base);
                font-weight: var(--joy-font-weight-bold);
                line-height: var(--joy-line-height-large);
                margin-right: calc(v-bind('props.titleMarginRight') * 1px);

                &--small {
                    font-size: var(--joy-font-size-primary-400);
                }

                &--large {
                    font-size: var(--joy-font-size-primary-600);
                }

                &__company {
                    width: 100%;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    white-space: nowrap;
                }
            }

            &__on-malt-badge {
                display: flex;
                flex-direction: row;
                align-items: center;
                background: var(--joy-color-primary-30);
                padding: 2px var(--joy-core-spacing-3);
                border-radius: var(--joy-core-radius-3);
                color: var(--joy-color-neutral-0);
                @include joy.getFontBySize(primary-200);
                height: 24px;
                margin-left: auto;

                &--small {
                    height: 20px;
                    padding: 2px var(--joy-core-spacing-2);
                }
            }

            &__on-malt-text {
                margin-left: var(--joy-core-spacing-2);
                white-space: nowrap;

                &--small {
                    margin-left: var(--joy-core-spacing-1);
                }
            }

            &__subtitle {
                font-size: var(--joy-font-size-primary-300);
                font-weight: var(--joy-font-weight-bold);
                margin-bottom: var(--joy-core-spacing-1);
                color: var(--joy-color-text-body);
                display: flex;
            }

            &__activity-area {
                white-space: nowrap;
                font-size: var(--joy-font-size-primary-200);
                font-weight: var(--joy-font-weight-normal);
            }

            &__others {
                display: flex;
                flex-direction: column;
                color: var(--joy-color-text-body);
                font-size: var(--joy-font-size-primary-300);
            }

            &__description {
                margin-top: var(--joy-core-spacing-3);
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                word-break: normal;
                overflow-wrap: anywhere;
                text-align: left;

                a {
                    flex: 0 0 auto;
                }

                ul {
                    margin-top: var(--joy-core-spacing-4);
                    margin-bottom: var(--joy-core-spacing-4);
                    list-style-type: disc;
                }
            }
        }
    }
</style>
